import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'framer-motion'
import { X } from 'lucide-react'
import { useEffect, useRef } from 'react'

export type ServiceBrandInfo = {
  id: string
  title: string
  description: string
  rowPosition: 'top' | 'bottom'
}

interface Props {
  brand: ServiceBrandInfo | null
  isMobile: boolean
  onClose: () => void
}

/**
 * ServiceInfoPopover — panel descriptivo para cards "Familia FITLAB".
 *
 *  - Desktop (≥768px): popover absoluto anclado al grid de cards. Si la card
 *    seleccionada está en la fila superior (`rowPosition='top'`) abre debajo
 *    del grid; si está en la fila inferior, abre arriba del grid.
 *  - Mobile (<768px): bottom sheet slide-up con backdrop + drag-to-dismiss.
 *
 * Cierre: click fuera, Escape, click <X>, swipe-down (solo mobile).
 *
 * 0 deps nuevas — Framer Motion + Tailwind, alineado al stack landing.
 */
export default function ServiceInfoPopover({ brand, isMobile, onClose }: Props) {
  const reduced = useReducedMotion()
  const panelRef = useRef<HTMLDivElement | null>(null)

  // Escape global cuando hay panel abierto
  useEffect(() => {
    if (!brand) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [brand, onClose])

  const handleDragEnd = (_e: unknown, info: PanInfo) => {
    if (info.offset.y > 100) onClose()
  }

  if (isMobile) {
    return (
      <AnimatePresence>
        {brand && (
          <>
            <motion.div
              key="backdrop"
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              className="fixed inset-0 z-40 bg-black/40"
            />
            <motion.div
              key="sheet"
              ref={panelRef}
              role="dialog"
              aria-modal="false"
              aria-labelledby={`brand-info-title-${brand.id}`}
              initial={reduced ? { opacity: 0 } : { y: '100%' }}
              animate={reduced ? { opacity: 1 } : { y: 0 }}
              exit={reduced ? { opacity: 0 } : { y: '100%' }}
              transition={
                reduced ? { duration: 0.2 } : { type: 'spring', stiffness: 350, damping: 30 }
              }
              drag={reduced ? false : 'y'}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
              className="fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-hidden rounded-t-3xl bg-white shadow-2xl"
            >
              <div aria-hidden="true" className="flex justify-center pb-1 pt-3">
                <span className="h-1.5 w-12 rounded-full bg-fitlab-neutral-200" />
              </div>
              <PanelBody brand={brand} onClose={onClose} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    )
  }

  // Desktop popover
  return (
    <AnimatePresence>
      {brand && (
        <motion.div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-labelledby={`brand-info-title-${brand.id}`}
          initial={
            reduced
              ? { opacity: 0 }
              : { opacity: 0, y: brand.rowPosition === 'top' ? -8 : 8, scale: 0.97 }
          }
          animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
          exit={
            reduced
              ? { opacity: 0 }
              : { opacity: 0, y: brand.rowPosition === 'top' ? -8 : 8, scale: 0.97 }
          }
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className={
            brand.rowPosition === 'top'
              ? 'relative z-30 mx-auto mt-4 w-full max-w-[420px] overflow-hidden rounded-2xl border border-fitlab-neutral-100 bg-white shadow-xl ring-1 ring-fitlab-neutral-100'
              : 'relative z-30 mx-auto mb-4 w-full max-w-[420px] overflow-hidden rounded-2xl border border-fitlab-neutral-100 bg-white shadow-xl ring-1 ring-fitlab-neutral-100'
          }
        >
          <span
            aria-hidden="true"
            className="absolute inset-x-0 top-0 mx-auto h-[3px] w-20 rounded-b-full bg-fitlab-gradient"
          />
          <PanelBody brand={brand} onClose={onClose} />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function PanelBody({ brand, onClose }: { brand: ServiceBrandInfo; onClose: () => void }) {
  return (
    <>
      <header className="flex items-start justify-between gap-4 border-b border-fitlab-neutral-100 px-6 py-4">
        <h3
          id={`brand-info-title-${brand.id}`}
          className="font-display text-lg font-extrabold tracking-tight text-fitlab-blue-hover"
        >
          {brand.title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-fitlab-neutral-600 transition-colors hover:bg-fitlab-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fitlab-green focus-visible:ring-offset-2"
        >
          <X className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        </button>
      </header>
      <p className="px-6 py-5 text-sm leading-relaxed text-fitlab-neutral-700 sm:text-base">
        {brand.description}
      </p>
    </>
  )
}
